import { useEffect, useState } from "react";
import api from "../lib/api";
import { AlertTriangle, Loader2, Trash2, X } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { useAuth } from "../lib/auth";

/**
 * Confirmation for permanently removing an inventory item.
 * Props: open, onOpenChange, item, onDelete (async)
 */
export default function DeleteInventoryItemDialog({ open, onOpenChange, item, onDelete }) {
  const { canDelete } = useAuth();
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && item) {
      setImpact(null);
      api.get(`/inventory/items/${item.id}/impact`).then((r) => setImpact(r.data));
    }
  }, [open, item]);

  const doDelete = async () => {
    setBusy(true);
    try {
      await onDelete();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg rounded-md" data-testid="delete-item-dialog">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-10 w-10 rounded-sm bg-red-100 text-red-700">
              <Trash2 size={20} />
            </div>
            <div>
              <AlertDialogTitle className="font-display text-2xl font-black tracking-tight text-slate-900">
                Remove this item?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-600 mt-1">
                Are you sure you want to remove <span className="font-semibold text-slate-900">{item?.name}</span> from
                the warehouse catalog? This action cannot be undone.
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        {impact === null ? (
          <div className="text-sm text-slate-500 font-mono py-4">Checking item history…</div>
        ) : (
          <div className="my-2 space-y-3">
            {impact.has_stock && (
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-md" data-testid="item-stock-warning">
                <div className="flex gap-3">
                  <AlertTriangle className="text-amber-700 shrink-0 mt-0.5" size={20} />
                  <div className="text-sm text-amber-900">
                    <div className="font-bold">Warehouse stock will be auto-adjusted</div>
                    <p className="mt-1">
                      Warehouse quantity of <span className="font-mono font-semibold">{impact.warehouse_quantity}</span>{" "}
                      {item?.unit || ""} will be zeroed out with a removal-adjustment entry (audited) before the item is
                      dropped from the catalog.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {impact.has_onsite_inventory && (
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-md" data-testid="item-onsite-warning">
                <div className="flex gap-3">
                  <AlertTriangle className="text-amber-700 shrink-0 mt-0.5" size={20} />
                  <div className="text-sm text-amber-900">
                    <div className="font-bold">On-site quantities at projects will be auto-adjusted</div>
                    <p className="mt-1">
                      One removal-adjustment entry will be logged per project so the audit trail matches reality.
                    </p>
                    <ul className="mt-2 space-y-1 font-mono text-xs">
                      {impact.onsite_at_projects.map((p) => (
                        <li key={p._id}>• {p.project_name} — <span className="font-semibold">{p.net}</span> zeroed</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {impact.has_history && !impact.has_stock && !impact.has_onsite_inventory && (
              <div className="bg-slate-50 border-l-4 border-slate-400 p-4 rounded-r-md" data-testid="item-history-warning">
                <div className="flex gap-3">
                  <AlertTriangle className="text-slate-700 shrink-0 mt-0.5" size={20} />
                  <div className="text-sm text-slate-800">
                    <div className="font-bold">This item has transaction history</div>
                    <p className="mt-1">
                      <span className="font-mono font-semibold">{impact.transaction_count}</span> receiving/transfer
                      transactions are attached. They will be moved to the audit log — never silently deleted.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!impact.has_history && !impact.has_stock && !impact.has_onsite_inventory && (
              <div className="text-sm text-slate-600">
                No stock, no on-site quantity, and no transaction history. Removal is safe.
              </div>
            )}
          </div>
        )}

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="delete-item-cancel-btn"
            className="rounded-sm"
          >
            <X size={16} className="mr-1.5" /> Cancel
          </Button>
          {canDelete ? (
            <Button
              onClick={doDelete}
              disabled={busy}
              data-testid="delete-item-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm font-semibold"
            >
              {busy ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <Trash2 size={16} className="mr-1.5" />}
              Remove Item
            </Button>
          ) : (
            <div className="text-xs text-slate-500 italic px-2">
              Only Admin or Installation Manager can remove items.
            </div>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
